import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { SlackAppCredentials } from "../../../pavilion/schemas";
import { api } from "../api/api";

// Query keys
const SLACK_CONFIG_KEY = "slack-config";
const MANIFEST_URL_KEY = "manifest-url";

// Query options for Slack config
export const slackConfigQueryOptions = (agentName: string) => ({
  queryKey: [SLACK_CONFIG_KEY, agentName],
  queryFn: () => api.getSlackConfig(agentName),
  refetchInterval: 5000, // Refetch every 5 seconds to catch OAuth completions
  refetchIntervalInBackground: false,
});

// Query options for manifest URL
export const manifestUrlQueryOptions = (agentName: string) => ({
  queryKey: [MANIFEST_URL_KEY, agentName],
  queryFn: () => api.getSlackManifestUrl(agentName),
});

// React Query hooks
export function useSlackConfig(agentName: string) {
  return useQuery(slackConfigQueryOptions(agentName));
}

export function useManifestUrl(agentName: string) {
  return useQuery(manifestUrlQueryOptions(agentName));
}

export function useStoreCredentials(agentName: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (credentials: SlackAppCredentials) =>
      api.storeSlackCredentials(agentName, credentials),
    onSuccess: () => {
      // Invalidate and refetch Slack config to update the UI
      queryClient.invalidateQueries({
        queryKey: [SLACK_CONFIG_KEY, agentName],
      });
    },
  });
}

export function useRemoveWorkspace(agentName: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (teamId: string) => api.removeSlackWorkspace(agentName, teamId),
    onSuccess: () => {
      // Invalidate and refetch Slack config to update the UI
      queryClient.invalidateQueries({
        queryKey: [SLACK_CONFIG_KEY, agentName],
      });
    },
  });
}
